import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription, take } from 'rxjs';
import { AudioPlayerComponent } from 'src/app/shared/components/audio-player/audio-player.component';
import { CommandType, TranscriptionOptionsComponent } from 'src/app/shared/components/audio-player/transcription-options.component';
import { TranscriptionModel, TranscriptionModels, TranscriptionPayload } from 'src/app/shared/components/recorders/audio-recorder/audio-recorder.dto';
import { TranscriptionProgressIndicatorComponent } from 'src/app/shared/components/recorders/audio-recorder/transcription-progress-indicator/transcription-progress-indicator.component';
import { CustomContextMenuItem } from 'src/app/shared/components/ui/custom-context-menu/custom-context-menu.component';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';

@Component({
  selector: 'app-audio-chunk-displayer',
  standalone: true,
  imports: [MatIconModule, AudioPlayerComponent, TranslateModule, TranscriptionProgressIndicatorComponent],
  styleUrls: ['./media-chunk-displayer.scss'],
  template: `
    @if (chunk(); as chunk) {
      @if (chunk.audioContent) {
        <div class="media-card">
          <mat-icon class="media-icon">graphic_eq</mat-icon>
          <div class="media-content">
            @if (isTranscribing()) {
              <app-transcription-progress-indicator [isActive]="true" [barsCount]="140" [fullWidth]="true" />
            } @else {
              <app-audio-player
                #audioPlayerRef
                [audioSource]="chunk.audioContent"
                [contrast]="true"
                [fullWidth]="true"
                [barsCount]="50"
                [additionalActions]="additionalActions"
                (ended)="onAudioEnded()"
                (played)="onPlay()"
                (paused)="onPause()"
                (click)="$event.stopPropagation()"
              />
            }
          </div>
        </div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioChunkDisplayerComponent {
  chunk = input.required<Chunk>();

  @ViewChild('audioPlayerRef') audioPlayerComponent?: AudioPlayerComponent;

  private contentStateService = inject(ContentStateService);
  private dynamicOverlayService = inject(DynamicOverlayService);
  private chunkService = inject(ChunkService);
  private translate = inject(TranslateService);
  public configService = inject(ConfigurationService);
  private destroyRef = inject(DestroyRef);

  private transcriptionOptionsSub: Subscription;

  private wasProgrammaticallyPlayed = false;
  private transcriptionModel: TranscriptionModel = TranscriptionModels.CHIRP3;
  additionalActions: CustomContextMenuItem[];

  isTranscribing = signal(false);

  constructor() {
    this.configService
      .getConfigValue('transcriptionSettings')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => {
        if (settings?.model) {
          this.transcriptionModel = settings.model;
        } else {
          this.transcriptionModel = TranscriptionModels.CHIRP3;
        }
      });

    this.additionalActions = [
      {
        id: 'transcribe-audio',
        icon: 'speech_to_text',
        text: 'Content.Chunk.TranscribeAudio',
        func: () => this.transcribeAudio(),
      },
    ];

    effect(() => {
      const command = this.contentStateService.playbackCommand();
      if (command?.chunkId === this.chunk()._id) {
        const player = this.audioPlayerComponent;
        if (!player) return;

        if (command.action === 'play' && player.isPaused()) {
          player.play();
          this.wasProgrammaticallyPlayed = true;
        } else if (command.action === 'pause' && !player.isPaused()) {
          player.pause();
        }
      }
    });
  }

  async transcribeAudio(): Promise<void> {
    const fileLink = this.chunk()?.editions?.find((ed) => ed.language === this.translate.currentLang)?.content;

    if (!fileLink) {
      console.warn('Transcription cancelled: No audio file available.');
      return;
    }
    this.transcriptionOptionsSub?.unsubscribe();
    const triggerElement = this.audioPlayerComponent.optionsButtonRef;
    const { ref: overlayRef, componentInstance } = this.dynamicOverlayService.open((triggerElement as any)._elementRef, TranscriptionOptionsComponent, { data: {} });
    this.transcriptionOptionsSub = componentInstance.commandSelected.subscribe(async (command: { type: CommandType }) => {
      if (command.type === 'start') {
        this.dynamicOverlayService.close();
        this.contentStateService.setLoading({ status: 'loading', message: this.translate.instant('Content.Chunk.TranscriptionInProgress'), reason: 'transcribe-audio' });
        this.isTranscribing.set(true);

        try {
          this.contentStateService.setLiveTranscriptionState(true, 'chunk');
          const chunkId = await this._createChunkForTranscription();
          await this.chunkService.setLiveTranscriptionChunkId(chunkId);
          
          const transcriptionData: TranscriptionPayload = {
            fileLink,
            language: this.translate.currentLang,
            settings: { model: this.transcriptionModel || TranscriptionModels.CHIRP3 },
            chunkId,
            contentID: this.contentStateService.contentId
          };

          const chunks = this.contentStateService.content();
          const targetChunk = chunks.find((c: any) => c._id === chunkId);
          let edition: any = null;
          if (targetChunk) {
            targetChunk.status = 'processing';
            this.contentStateService.updateChunkLocally(chunkId, targetChunk);
            edition = targetChunk.editions?.find((e: any) => e.language === this.translate.currentLang) || targetChunk.editions?.[0];
          }

          await this.chunkService.transcribeAudio(transcriptionData);

          if (targetChunk) {
            this.contentStateService.openCanvas({
              title: this.translate.instant('Global.Transcript'),
              html: `<p><i>${this.translate.instant('Content.Chunk.TranscriptionInProgress')}</i></p>`,
              source: 'transcript',
              chunkId: chunkId,
              contentId: this.contentStateService.contentId,
              edition: edition,
            });

            this.contentStateService.openContentGenerator({
              initialSubject: this.translate.instant('Global.Transcript'),
              context: {
                id: chunkId,
                title: this.translate.instant('Global.Transcript'),
                name: this.translate.instant('Global.Transcript'),
                type: 'chunk'
              },
              isAuto: true,
            });
          }

          this.contentStateService.setLoading({ status: 'success', message: this.translate.instant('Content.Chunk.TranscriptionStarted') || 'Transcription started in background', reason: 'transcribe-audio' });
        } catch (err) {
          console.error('An error occurred during the transcription process:', err);
          this.contentStateService.setLoading({ status: 'error', message: this.translate.instant('Content.Chunk.TranscriptionFailed'), reason: 'transcribe-audio' });
        } finally {
          setTimeout(() => this.contentStateService.setLoading({ status: 'idle' }), 2000);
          this.contentStateService.setLiveTranscriptionState(false);
          this.isTranscribing.set(false);
        }
      } else {
        this.dynamicOverlayService.close();
      }
    });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => this.transcriptionOptionsSub?.unsubscribe());
  }

  onAudioEnded(): void {
    if (this.wasProgrammaticallyPlayed) {
      this.contentStateService.handleAudioEnded();
    }
    this.wasProgrammaticallyPlayed = false; // Reset for next time
  }

  onUserInteraction(): void {
    if (this.wasProgrammaticallyPlayed) {
      this.contentStateService.clearPlaybackCommand();
    }
  }

  onPlay(): void {
    this.onUserInteraction();
    this.contentStateService.reportPlaybackStarted(this.chunk()._id);
  }

  onPause(): void {
    this.onUserInteraction();
    this.contentStateService.reportPlaybackPaused(this.chunk()._id);
  }

  private async _createChunkForTranscription(): Promise<string> {
    const chunkData = {
      content: '',
      type: 'canvas-transcript',
      status: 'processing',
      language: this.translate.currentLang,
      contentID: this.contentStateService.contentId,
      courseID: this.contentStateService.courseId,
      blogID: this.contentStateService.blogId,
      noteID: this.contentStateService.noteId,
      lecture: this.contentStateService.lecture,
      section: this.contentStateService.section,
      contentName: this.contentStateService.contentName,
      contentType: this.contentStateService.contentType,
      generateVoice: false,
      generateTranslation: false,
      generateEmbeddings: false,
    };
    const res = await firstValueFrom(this.chunkService.createChunk({ chunks: [chunkData] }));
    const newChunk = res[0].chunk;
    this.contentStateService.setHighlightedChunkId(newChunk._id);
    return newChunk._id;
  }
}
